/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import PropTypes from 'prop-types';
import React from 'react';

// eslint-disable-next-line no-unused-vars
import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import Link from '@atlaskit/link/link';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';
import SectionMessageAction from '@atlaskit/section-message/message-action';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys as storageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { useIsSiteAdmin } from '@confluence/current-user/entry-points/useIsSiteAdmin.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';

const DISMISS_STORAGE_KEY_PREFIX = storageKeys.CONNECT_EOS_BANNER_DISMISSED;
const DEVELOPER_CANARY_PROGRAM_URL =
	'https://developer.atlassian.com/cloud/jira/platform/developer-canary-program/';
const FORGE_CHANGELOG_URL = 'https://developer.atlassian.com/platform/forge/changelog/';
const SECONDS_PER_DAY = 24 * 60 * 60;
const MS_PER_DAY = SECONDS_PER_DAY * 1000;

function getDismissStorageKey(appKey) {
	return `${appKey}-${DISMISS_STORAGE_KEY_PREFIX}`;
}

function isDismissed(appKey) {
	// time when the popup was originally dismissed in milliseconds since epoch
	const dismissedAt = confluenceLocalStorageInstance.getItem(getDismissStorageKey(appKey));
	if (!dismissedAt) {
		return false; // we didn't previously dismiss it, or else the local storage expired
	}
	const dismissedTimestamp = Number(dismissedAt);
	if (isNaN(dismissedTimestamp)) {
		return false;
	}
	return (
		Date.now() - dismissedTimestamp <
		expVal('customer-messaging-iframe-banner-dismiss-days', 'value', 30) * // default is 30 days
			MS_PER_DAY
	);
}

// Replicates @atlaskit/inline-message styles instead of using it directly — InlineMessage
// hardcodes shouldRenderToParent={true}, mispositioning the popup in overflow:hidden/transform
// ancestors. @atlaskit/popup defaults to shouldRenderToParent={false} (portals to body).
const styles = cssMap({
	root: {
		display: 'inline-block',
		maxWidth: '100%',
		color: token('color.icon.warning'),
	},
	pressable: {
		display: 'inline-flex',
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		whiteSpace: 'nowrap',
		borderRadius: token('radius.medium'),
		paddingBlock: token('space.0'),
		paddingInline: token('space.0'),
		position: 'relative',
		alignItems: 'baseline',
		maxWidth: '100%',
		height: 'auto',
		width: 'auto',
		verticalAlign: 'baseline',
		font: token('font.body'),
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	iconWrapper: {
		display: 'flex',
		alignItems: 'center',
		flex: '0 0 auto',
		color: token('color.icon.warning'),
	},
	content: {
		paddingInlineStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		maxWidth: `${8 * 56}px`,
		maxHeight: `${8 * 56}px`,
		overflow: 'auto',
		color: token('color.text'),
	},
	text: {
		whiteSpace: 'normal',
	},
});

export function ConnectEOSPopup({ appKey }) {
	const [dismissed, setDismissed] = React.useState(() => isDismissed(appKey));
	const [isOpen, setIsOpen] = React.useState(false);
	const { isSiteAdmin } = useIsSiteAdmin();
	const { Popup } = useChoreographerAssets();
	// Defers Popup (portal-based) until after hydration to avoid SSR mismatches.
	const isServerPlaceholder = useRenderServerPlaceholder();

	const handleClose = React.useCallback(() => setIsOpen(false), []);
	const handleToggle = React.useCallback(() => setIsOpen((prev) => !prev), []);

	const dismiss = React.useCallback(() => {
		// Close the popup first so Popup can perform cleanup (focus restoration,
		// scroll listener teardown) before the component unmounts.
		setIsOpen(false);
		const dismissDurationMs =
			expVal('customer-messaging-iframe-banner-dismiss-days', 'value', 30) * // default is 30 days
			MS_PER_DAY;
		const key = getDismissStorageKey(appKey);
		const value = Date.now();
		const expiry = dismissDurationMs / 1000;
		confluenceLocalStorageInstance.setItem(
			key,
			value, // value in milliseconds
			expiry, // expiry in seconds
		);
		setDismissed(true);
	}, [appKey]);

	const renderContent = React.useCallback(
		() => (
			<Box xcss={styles.content}>
				<Text as="p" xcss={styles.text}>
					{isSiteAdmin ? (
						<>
							{
								"This app is running on a platform that'll be unsupported after December 2026. Check "
							}
							<Link href={`${CONTEXT_PATH}/plugins/servlet/upm`}>Connected apps</Link>
							{' to see what options are available for all similar apps.'}
						</>
					) : (
						"This app is running on a platform that'll be unsupported after December 2026. To explore your options, contact your site admin."
					)}
				</Text>
				{fg('customer-messaging-is-dev-canary') && (
					<Text as="p" xcss={styles.text}>
						{"You're seeing this message because your tenant is enrolled in the "}
						<Link href={DEVELOPER_CANARY_PROGRAM_URL}>Developer Canary Program</Link>
						{'. For updates on when this goes live in production, watch the '}
						<Link href={FORGE_CHANGELOG_URL}>Forge changelog</Link>.
					</Text>
				)}
				<Text as="p" xcss={styles.text}>
					<SectionMessageAction onClick={dismiss} key="dismiss">
						Dismiss
					</SectionMessageAction>
				</Text>
			</Box>
		),
		[isSiteAdmin, dismiss],
	);

	// Exclude isOpen from deps — Popup injects aria-expanded via triggerProps, and including
	// it would recreate the trigger on every toggle, causing focus loss.
	const renderTrigger = React.useCallback(
		({ ref, onClick: triggerOnClick, ...triggerProps }) => (
			<Pressable
				{...triggerProps}
				ref={ref}
				xcss={styles.pressable}
				onClick={(event) => {
					triggerOnClick?.(event);
					handleToggle();
				}}
				aria-haspopup="dialog"
			>
				<Inline as="span" space="space.050" alignBlock="center">
					<span css={styles.iconWrapper}>
						<WarningIcon label="warning inline message" color="currentColor" spacing="spacious" />
					</span>
				</Inline>
			</Pressable>
		),
		[handleToggle],
	);

	if (dismissed) {
		return null;
	}

	return (
		<div css={styles.root} data-testid="inline-banner">
			{!isServerPlaceholder ? (
				<Popup
					isOpen={isOpen}
					onClose={handleClose}
					placement="bottom-start"
					strategy="absolute"
					role="dialog"
					// eslint-disable-next-line no-restricted-syntax
					aria-label="App end-of-support warning"
					content={renderContent}
					trigger={renderTrigger}
				/>
			) : (
				// SSR/hydration fallback: render trigger without Popup wiring.
				// Disabled to prevent setIsOpen firing before Popup is ready.
				renderTrigger({ ref: null, disabled: true })
			)}
		</div>
	);
}

ConnectEOSPopup.propTypes = {
	appKey: PropTypes.string,
};
